// Pronunciation: sound tiles, text to read aloud, and words marked by how clearly you said them.

import { PRON_TARGET, type Tone, toneOf } from '@shared/scoring';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';

/** IPA symbol in a tile, e.g. /θ/. */
export function SoundTile({ symbol, small }: { symbol: string; small?: boolean }) {
  return <span className={cx('sound', small && 'sm')}>{symbol}</span>;
}

/** Large text to read aloud. Wrap the focus sounds in <u>. */
export function SayText({ children }: { children: ReactNode }) {
  return <p className="say-text">{children}</p>;
}

export type ScoredWord = { word: string; score: number };

/** Your sentence with each word marked: plain clear, dotted almost, amber underline unclear. */
export function PronLine({ words, onWord }: { words: readonly ScoredWord[]; onWord?: (w: ScoredWord) => void }) {
  return (
    <p className="pron-line">
      {words.map((w, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: words are positional in the sentence.
        <span key={i}>
          <button
            type="button"
            className={cx('w', toneOf(w.score, PRON_TARGET))}
            onClick={() => onWord?.(w)}
            aria-label={`${w.word}: ${w.score}`}
          >
            {w.word}
          </button>{' '}
        </span>
      ))}
    </p>
  );
}

/** Legend for PronLine. */
export function PronKey() {
  const keys: Array<[Tone, string]> = [
    ['good', 'Clear'],
    ['ok', 'Almost'],
    ['low', 'Unclear'],
  ];
  return (
    <p className="t-3 xs">
      {keys.map(([tone, label]) => (
        <span key={tone}>
          <i className={cx('w-key', tone)} />
          {label}
        </span>
      ))}
    </p>
  );
}
